// The hazard markers resolve their `icon` from the map style's sprite, as the
// POI layer's `puck_icon_*` do. A style without the HiiKER sprite (a plain
// Mapbox satellite or outdoors style, or one added later) would draw nothing,
// so when Mapbox reports a missing image that is one of the hazard marker names
// — and only then — the bundled PNG of the same icon is added under that name.
// Mapbox redraws the tiles that use an image once it is added.
//
// Collaborators are injected so the boundary is unit-tested without Mapbox or
// a browser image decoder; `addBundledHazardImage` wires the real ones.

import { HAZARD_ICON_PIXEL_RATIO, hazardIconUrl } from './hazardIconAssets';
import { isHazardSpriteName } from './hazardSprite';

/** The map methods this needs (a mapboxgl.Map satisfies it). */
export interface MissingImageMap {
  hasImage(id: string): boolean;
  addImage(id: string, image: any, options?: { pixelRatio?: number }): void;
}

export type ImageLoader = (url: string) => Promise<any>;

export interface MissingImageDeps {
  urlFor: (name: string) => string | null;
  load: ImageLoader;
  pixelRatio: number;
}

/** Loads an image through an <img> element: same-origin, no CORS, decoded by the browser. */
export function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`[trail_hazards] could not load ${url}`));
    image.src = url;
  });
}

const DEFAULT_DEPS: MissingImageDeps = {
  urlFor: hazardIconUrl,
  load: loadImageElement,
  pixelRatio: HAZARD_ICON_PIXEL_RATIO,
};

/**
 * Handles one `styleimagemissing` id. Returns the promise of the add when it
 * took the id on (a hazard marker the map doesn't have and isn't already
 * loading), or null when the id is someone else's — POI, waypoint, or any
 * other layer's image is left for its own handler. Never rejects.
 */
export function createMissingHazardImageHandler(deps: MissingImageDeps = DEFAULT_DEPS) {
  // Per map: a style switch clears the images, and each map loads its own.
  const loading = new WeakMap<MissingImageMap, Set<string>>();

  return function handleMissingImage(map: MissingImageMap, id: unknown): Promise<boolean> | null {
    if (!map || !isHazardSpriteName(id)) return null;
    if (map.hasImage(id)) return null;
    const url = deps.urlFor(id);
    if (!url) return null;

    let pending = loading.get(map);
    if (!pending) {
      pending = new Set();
      loading.set(map, pending);
    }
    if (pending.has(id)) return null;
    pending.add(id);

    return deps.load(url)
      .then((image) => {
        // The sprite may have arrived meanwhile (style switch back).
        if (map.hasImage(id)) return false;
        map.addImage(id, image, { pixelRatio: deps.pixelRatio });
        return true;
      })
      .catch((error) => {
        console.warn('[trail_hazards] could not add the fallback marker', id, error);
        return false;
      })
      .finally(() => {
        pending?.delete(id);
      });
  };
}

/** The page's handler with the bundled PNGs. */
export const addBundledHazardImage = createMissingHazardImageHandler();
